import { Button, Dialog, TextField, Typography } from "@platform/design-system";
import React, { useEffect, useRef, useState } from "react";

export interface CommentDialogProps {
  open: boolean;
  title?: string;
  currentComment: string;
  /** ISO timestamp — when the review was first created */
  createdAt?: string;
  /** ISO timestamp — when the review was last updated */
  updatedAt?: string;
  maxLength?: number;
  onSubmit: (comment: string) => void;
  onCancel: VoidFunction;
}

const formatTimestamp = (iso: string): string =>
  new Intl.DateTimeFormat(undefined, {
    dateStyle: "medium",
    timeStyle: "short",
  }).format(new Date(iso));

export const CommentDialog: React.FC<CommentDialogProps> = ({
  open,
  title = "Needs Review",
  currentComment,
  createdAt,
  updatedAt,
  maxLength,
  onSubmit,
  onCancel,
}) => {
  const [value, setValue] = useState(currentComment);
  const isTooLong = maxLength !== undefined && value.length > maxLength;
  const error = isTooLong
    ? `Comment cannot exceed ${maxLength} characters.`
    : "";
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const dialogRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setValue(currentComment);
  }, [currentComment, open]);

  useEffect(() => {
    if (open) {
      setTimeout(() => {
        dialogRef.current?.focus();
        setTimeout(() => {
          const el = inputRef.current;
          if (el) {
            el.focus();
            const len = el.value.length;
            el.setSelectionRange(len, len);
          }
        }, 50);
      }, 50);
    }
  }, [open]);

  return (
    <Dialog
      open={open}
      title={title}
      onClose={onCancel}
      maxWidth="sm"
      fullWidth
      PaperProps={{ tabIndex: -1, ref: dialogRef }}
      actions={
        <>
          <Button onClick={onCancel} variant="secondary">
            Cancel
          </Button>
          <Button
            onClick={() => onSubmit(value)}
            variant="primary"
            disabled={isTooLong}
          >
            Save
          </Button>
        </>
      }
    >
      <TextField
        id="comment-dialog-textfield"
        name="comment"
        inputRef={inputRef}
        margin="dense"
        label="Comment (optional)"
        type="text"
        fullWidth
        multiline
        minRows={3}
        value={value}
        onChange={(e) => setValue(e.target.value)}
        error={isTooLong}
        helperText={error}
        slotProps={{
          input: {
            id: "comment-dialog-textfield",
            name: "comment",
          },
        }}
      />

      {updatedAt || createdAt ? (
        <Typography
          variant="caption"
          display="block"
          color="textSecondary"
          mt={2}
        >
          {updatedAt && updatedAt !== createdAt
            ? `Edited at: ${formatTimestamp(updatedAt)}`
            : createdAt
              ? `Created at: ${formatTimestamp(createdAt)}`
              : null}
        </Typography>
      ) : null}
    </Dialog>
  );
};

// Set display name for better debugging
CommentDialog.displayName = "CommentDialog";
export default CommentDialog;
